import type {UseQueryResult} from 'react-query';
import {useQuery} from 'react-query';
import {z} from 'zod';
import {apiUrl} from '@/utils/api';
import {type DigitalCode} from '@/types/DigitalCode';
import {useUserContext} from '@/components/Providers/UserContext';

type UseFetchDigitalCodesResponse = {
    data : DigitalCode[],
    errorCode : number,
    message : string,
};

const digitalCodeSchema = z.object({
    code : z.string(),
    course : z.string(),
    description : z.string(),
    instructions : z.string(),
    releaseDate : z.string(),
});

const useFetchDigitalCodesResponseSchema = z.object({
    data : z.array(digitalCodeSchema),
    errorCode : z.number(),
    message : z.string(),
});

const useFetchDigitalCodes = () : UseQueryResult<UseFetchDigitalCodesResponse> => {
    const {user, setUser} = useUserContext();

    return useQuery(
        ['digital-codes'],
        async ({signal}) => {
            const response = await fetch(
                apiUrl('/v1/digital-codes').toString(),
                {
                    signal: (signal ?? null),
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.jwt}`,
                    },
                }
            );

            if (response.status === 401) {
                setUser(null);
            }

            if (!response.ok) {
                throw new Error('Unable to fetch digital-codes.');
            }

            const json = await response.json() as UseFetchDigitalCodesResponse;

            try {
                useFetchDigitalCodesResponseSchema.parse(json);
            } catch (error: unknown) {
                console.log('error', error);
                throw new Error('Unable to parse digital-codes response.');
            }

            return json;
        },
        {
            enabled: user !== undefined
        }
    );
};

export default useFetchDigitalCodes;
